/** Compact AI launcher for the Action Centre: one brand-aware bar that opens a drawer hosting the existing Copilot + Ask AI tools. */
import { useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useCurrentEstablishmentName } from "@/components/app/EstablishmentSwitcher";
import CopilotBox from "@/components/app/CopilotBox";
import AskSuntronixAI from "@/components/askai/AskSuntronixAI";

const brandLabel = (n) => (!n || n === "SUNTRONIX" || n === "ONE GROUP" ? "ONE" : n.toUpperCase());

export function AiLauncher() {
  const [open, setOpen] = useState(false);
  const name = brandLabel(useCurrentEstablishmentName());
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="ai-launcher-btn"
        className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 text-left transition-[border-color,box-shadow] duration-150 hover:border-sky-300 hover:shadow-sm">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-slate-900 text-sky-300"><Sparkles className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-slate-900" data-testid="ai-launcher-title">Ask {name}</span>
          <span className="block truncate text-[11px] text-slate-500">Customers, quotes, orders, finance, devices — answers from records you can see</span>
        </span>
        <span className="hidden items-center gap-1 text-[11px] font-medium text-sky-700 sm:inline-flex">Open <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto p-4 sm:max-w-xl sm:p-5" data-testid="ai-launcher-drawer">
          <SheetHeader className="mb-3 text-left">
            <SheetTitle className="text-base" data-testid="ai-drawer-title">Ask {name}</SheetTitle>
            <SheetDescription className="text-[12px]">Read-only answers within your role permissions. Nothing is changed without your confirmation.</SheetDescription>
          </SheetHeader>
          <Tabs defaultValue="records">
            <TabsList className="mb-3 grid w-full grid-cols-2">
              <TabsTrigger value="records" data-testid="ai-tab-records">Business records</TabsTrigger>
              <TabsTrigger value="estate" data-testid="ai-tab-estate">Device estate</TabsTrigger>
            </TabsList>
            <TabsContent value="records"><CopilotBox compact title={`Ask ${name} · Business records`} scopeNote="Scope: ONE GROUP business records you are permitted to see — not filtered by the selected company" /></TabsContent>
            <TabsContent value="estate"><AskSuntronixAI compact title={`Ask ${name} · Device estate`} /></TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>
    </>
  );
}
